/**
 * How It Works Lumeska — cinematic editorial background.
 * Baseline (heading, steps, fully-lit timeline) is plain HTML/CSS — works
 * with zero JS. GSAP + ScrollTrigger are fetched from CDN only when motion
 * is allowed, after idle, and layered on top for: the line-by-line heading
 * reveal, the very subtle background parallax, and — the core interaction —
 * a scroll-scrubbed timeline. The light in the timeline is never driven by
 * a timer: it is a direct function of scroll position, recalculated every
 * frame GSAP's ScrollTrigger reports, so it advances exactly as fast (or
 * slow, or not at all) as the user scrolls.
 */
(function () {
  const GSAP_URL = 'https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/gsap.min.js';
  const SCROLLTRIGGER_URL = 'https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/ScrollTrigger.min.js';

  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let gsapLoadPromise = null;

  function loadScript(src) {
    return new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        if (existing.dataset.loaded === 'true') resolve();
        else existing.addEventListener('load', () => resolve());
        return;
      }
      const script = document.createElement('script');
      script.src = src;
      script.defer = true;
      script.addEventListener('load', () => {
        script.dataset.loaded = 'true';
        resolve();
      });
      script.addEventListener('error', reject);
      document.head.appendChild(script);
    });
  }

  function loadGsap() {
    if (gsapLoadPromise) return gsapLoadPromise;
    gsapLoadPromise = loadScript(GSAP_URL)
      .then(() => loadScript(SCROLLTRIGGER_URL))
      .then(() => {
        if (window.gsap && window.ScrollTrigger) {
          window.gsap.registerPlugin(window.ScrollTrigger);
        }
        return window.gsap;
      })
      .catch(() => null);
    return gsapLoadPromise;
  }

  function whenIdle(callback) {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(callback, { timeout: 1500 });
    } else {
      window.setTimeout(callback, 300);
    }
  }

  /**
   * Wraps every word of an element's text in a two-layer span (outer =
   * overflow mask, inner = the piece that actually translates) without
   * disturbing any nested inline markup (<em>, <strong>, …), then groups
   * the resulting words into visual lines by comparing rendered offsetTop.
   * Returns an array of line arrays, each containing the inner spans for
   * that line — ready to animate one line at a time.
   */
  function splitHeadingIntoLines(el) {
    const words = [];

    function walk(node) {
      Array.from(node.childNodes).forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          const parts = child.textContent.split(/(\s+)/).filter((part) => part !== '');
          const frag = document.createDocumentFragment();
          parts.forEach((part) => {
            if (/^\s+$/.test(part)) {
              frag.appendChild(document.createTextNode(part));
              return;
            }
            const mask = document.createElement('span');
            mask.className = 'hiw-word';
            const inner = document.createElement('span');
            inner.className = 'hiw-word-inner';
            inner.textContent = part;
            mask.appendChild(inner);
            frag.appendChild(mask);
            words.push(inner);
          });
          child.replaceWith(frag);
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          walk(child);
        }
      });
    }

    walk(el);

    const lines = [];
    let lastTop = null;
    words.forEach((word) => {
      const top = word.parentElement.offsetTop;
      if (lastTop === null || Math.abs(top - lastTop) > 4) {
        lines.push([]);
        lastTop = top;
      }
      lines[lines.length - 1].push(word);
    });
    return lines;
  }

  class HowItWorksLumeska {
    constructor(root) {
      this.root = root;
      this.bg = root.querySelector('[data-hiw-bg]');
      this.bgImage = root.querySelector('.how-it-works__bg-image');
      this.shadow = root.querySelector('[data-hiw-shadow]');
      this.list = root.querySelector('[data-hiw-list]');
      this.eyebrow = root.querySelector('[data-hiw-eyebrow]');
      this.heading = root.querySelector('[data-hiw-heading]');
      this.steps = Array.from(root.querySelectorAll('[data-hiw-step]'));
      this.reducedMotion = prefersReducedMotion();
      this.activeIndex = -1;

      if (!this.reducedMotion) {
        whenIdle(() => this.setupPremiumMotion());
      }
    }

    setupPremiumMotion() {
      loadGsap().then((gsap) => {
        if (!gsap || !window.ScrollTrigger) return;
        this.animateHeading(gsap);
        this.animateParallax(gsap);
        this.setupScrollScrubbedTimeline(gsap);
      });
    }

    animateHeading(gsap) {
      if (this.eyebrow) {
        gsap.fromTo(
          this.eyebrow,
          { opacity: 0, y: 14 },
          {
            opacity: 1,
            y: 0,
            duration: 0.9,
            ease: 'power2.out',
            scrollTrigger: { trigger: this.root, start: 'top 78%' },
          }
        );
      }

      if (this.heading) {
        const lines = splitHeadingIntoLines(this.heading);
        if (lines.length) {
          const tl = gsap.timeline({
            scrollTrigger: { trigger: this.root, start: 'top 72%' },
          });
          lines.forEach((line, index) => {
            tl.fromTo(
              line,
              { yPercent: 110 },
              { yPercent: 0, duration: 1, ease: 'expo.out', stagger: 0.035 },
              index * 0.16
            );
          });
        }
      }
    }

    // The background photograph stays almost perfectly still while the
    // user reads — a couple of percent of vertical drift, nothing more —
    // with the plant-shadow silhouette moving at its own, distinct rate
    // for a faint sense of depth. Neither ever touches the text content,
    // which never moves at all.
    animateParallax(gsap) {
      if (this.bg) {
        gsap.to(this.bg, {
          yPercent: 2,
          ease: 'none',
          scrollTrigger: {
            trigger: this.root,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
          },
        });
      }

      if (this.shadow) {
        gsap.to(this.shadow, {
          yPercent: -1.5,
          ease: 'none',
          scrollTrigger: {
            trigger: this.root,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
          },
        });
      }
    }

    // The core interaction: the timeline's light is a pure function of
    // scroll position within the list's own scroll range, recalculated on
    // every ScrollTrigger update (tied to the scrollbar via scrub: true —
    // no numeric smoothing, so there is zero lag and zero autoplay). Scroll
    // down slowly and the line advances slowly; stop scrolling and it stops
    // exactly where it is.
    setupScrollScrubbedTimeline(gsap) {
      if (!this.list || this.steps.length < 2) return;
      const segments = this.steps.length - 1;

      // The widest natural range available: from the moment the list first
      // appears at the bottom of the viewport to the moment it's about to
      // leave at the top. A short range made the fill feel rushed even
      // though it was tracking the scrollbar exactly — the scroll distance
      // itself was just too small. This roughly doubles it.
      window.ScrollTrigger.create({
        trigger: this.list,
        start: 'top 92%',
        end: 'bottom 8%',
        scrub: true,
        onUpdate: (self) => this.updateTimelineProgress(self.progress, segments),
      });
    }

    updateTimelineProgress(progress, segments) {
      let activeIndex = 0;

      this.steps.forEach((step, index) => {
        if (index >= segments) return; // last step has no outgoing line
        const segStart = index / segments;
        const segEnd = (index + 1) / segments;
        const local = Math.max(0, Math.min(1, (progress - segStart) / (segEnd - segStart)));
        step.style.setProperty('--line-progress', local);
        if (progress >= segEnd) activeIndex = index + 1;
      });

      if (activeIndex !== this.activeIndex) {
        this.steps.forEach((step, index) => {
          step.classList.toggle('is-active', index === activeIndex);
          step.classList.toggle('is-passed', index < activeIndex);
        });
        this.activeIndex = activeIndex;
      }
    }
  }

  function initAll(root = document) {
    root.querySelectorAll('.how-it-works:not([data-hiw-initialized])').forEach((el) => {
      el.setAttribute('data-hiw-initialized', 'true');
      new HowItWorksLumeska(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  }
})();
